import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { API_CONFIG, isMultiTenantMode } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth moet gebruikt worden binnen een AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check voor bestaande auth bij component mount (alleen in multi-tenant mode)
  useEffect(() => {
    if (isMultiTenantMode()) {
      checkExistingAuth();
    } else {
      // In single tenant mode, geen auth nodig
      setLoading(false);
    }
  }, []);

  // Automatische token refresh timer
  useEffect(() => {
    if (!isAuthenticated || !isMultiTenantMode()) {
      return;
    }

    const checkTokenExpiry = async () => {
      const token = Cookies.get('auth_token');
      if (!token) {
        logout();
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const timeUntilExpiry = decoded.exp * 1000 - Date.now();
        
        // Refresh token als het binnen 5 minuten verloopt
        if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
          console.log('🔄 Token verloopt binnenkort, refreshing...');
          await refreshToken();
        } else if (timeUntilExpiry <= 0) {
          // Token is al verlopen
          console.log('⚠️ Token is verlopen, logging out...');
          logout();
        }
      } catch (error) {
        console.error('Error checking token expiry:', error);
        logout();
      }
    };

    // Check direct bij start
    checkTokenExpiry();
    
    // Check elke minuut
    const interval = setInterval(checkTokenExpiry, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const refreshToken = async () => {
    // Alleen refresh in multi-tenant mode
    if (!isMultiTenantMode()) {
      return false;
    }

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        return false;
      }

      const response = await fetch(`${API_CONFIG.centralBackendURL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update token in cookie
        Cookies.set('auth_token', data.access_token, { 
          expires: 1, // 1 dag
          secure: window.location.protocol === 'https:',
          sameSite: 'Strict'
        });

        // Update user data
        const decoded = jwtDecode(data.access_token);
        setUser(decoded);
        
        console.log('✅ Token successfully refreshed');
        return true;
      } else {
        console.error('Failed to refresh token:', response.status);
        logout();
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
      return false;
    }
  };

  const checkExistingAuth = async () => {
    try {
      const token = Cookies.get('auth_token');
      const envId = localStorage.getItem('selected_environment');
      
      if (token) {
        const decoded = jwtDecode(token);
        
        // Check of token nog geldig is
        if (decoded.exp * 1000 > Date.now()) {
          setUser(decoded);
          setIsAuthenticated(true);
          
          // Laad environments
          await loadEnvironments();
          
          // Herstel geselecteerde environment als deze bestaat
          if (envId) {
            const savedEnv = localStorage.getItem(`environment_${envId}`);
            if (savedEnv) {
              const environment = JSON.parse(savedEnv);
              setSelectedEnvironment(environment);
              
              // Check of we een API key hebben, zo niet, haal deze op
              if (!environment.api_key) {
                console.log('🔄 Refreshing API key for restored environment...');
                try {
                  const response = await fetch(`${API_CONFIG.centralBackendURL}/api/v1/environments/${envId}/api-key`, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });

                  if (response.ok) {
                    const data = await response.json();
                    const environmentWithApiKey = {
                      ...environment,
                      api_key: data.api_key
                    };
                    
                    setSelectedEnvironment(environmentWithApiKey);
                    localStorage.setItem(`environment_${envId}`, JSON.stringify(environmentWithApiKey));
                    console.log(`✅ API key restored for environment: ${environment.name}`);
                  }
                } catch (error) {
                  console.error('Could not restore API key:', error);
                }
              } else {
                console.log(`✅ Environment restored with API key: ${environment.name}`);
              }
            }
          }
        } else {
          // Token is verlopen
          logout();
        }
      }
    } catch (error) {
      console.error('Error checking existing auth:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loadEnvironments = async () => {
    // Alleen laden in multi-tenant mode
    if (!isMultiTenantMode()) {
      return;
    }
    
    try {
      const token = Cookies.get('auth_token');
      const response = await fetch(`${API_CONFIG.centralBackendURL}/api/v1/environments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // API geeft een object terug met environments property
        setEnvironments(data.environments || []);
      }
    } catch (error) {
      console.error('Error loading environments:', error);
    }
  };

  const login = async (token) => {
    // Alleen login in multi-tenant mode
    if (!isMultiTenantMode()) {
      return false;
    }
    
    try {
      const decoded = jwtDecode(token);
      
      // Sla token op in httpOnly cookie (indien mogelijk) of localStorage
      Cookies.set('auth_token', token, { 
        expires: 1, // 1 dag
        secure: window.location.protocol === 'https:',
        sameSite: 'Strict'
      });
      
      setUser(decoded);
      setIsAuthenticated(true);
      
      // Laad beschikbare environments
      await loadEnvironments();
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    Cookies.remove('auth_token');
    localStorage.removeItem('selected_environment');
    
    // Clear environment data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('environment_')) {
        localStorage.removeItem(key);
      }
    });
    
    setUser(null);
    setIsAuthenticated(false);
    setSelectedEnvironment(null);
    setEnvironments([]);
  };

  const selectEnvironment = async (environment) => {
    // Alleen environment selectie in multi-tenant mode
    if (!isMultiTenantMode()) {
      return;
    }
    
    try {
      // Haal de API key op voor directe verbinding
      const token = Cookies.get('auth_token');
                const response = await fetch(`${API_CONFIG.centralBackendURL}/api/v1/environments/${environment.id}/api-key`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const environmentWithApiKey = {
          ...environment,
          api_key: data.api_key
        };
        
        setSelectedEnvironment(environmentWithApiKey);
        localStorage.setItem('selected_environment', environment.id);
        localStorage.setItem(`environment_${environment.id}`, JSON.stringify(environmentWithApiKey));
        
        console.log(`✅ Environment geselecteerd: ${environment.name} - Direct verbinding naar ${environment.api_url}`);
      } else {
        console.error('Could not retrieve API key for environment');
        // Fallback: selecteer environment zonder API key
        setSelectedEnvironment(environment);
        localStorage.setItem('selected_environment', environment.id);
        localStorage.setItem(`environment_${environment.id}`, JSON.stringify(environment));
      }
    } catch (error) {
      console.error('Error selecting environment:', error);
      // Fallback: selecteer environment zonder API key
      setSelectedEnvironment(environment);
      localStorage.setItem('selected_environment', environment.id);
      localStorage.setItem(`environment_${environment.id}`, JSON.stringify(environment));
    }
  };

  const clearEnvironmentSelection = () => {
    setSelectedEnvironment(null);
    localStorage.removeItem('selected_environment');
  };

  const getAuthHeaders = () => {
    // Alleen auth headers in multi-tenant mode
    if (!isMultiTenantMode()) {
      return {};
    }
    
    const token = Cookies.get('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const updateProfile = (updatedUser) => {
    // Update user state met nieuwe gegevens
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUser
    }));
  };

  const value = {
    user,
    isAuthenticated,
    selectedEnvironment,
    environments,
    loading,
    login,
    logout,
    refreshToken,
    selectEnvironment,
    clearEnvironmentSelection,
    loadEnvironments,
    getAuthHeaders,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 